import {
  Button,
  TextField,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InputChangeEvent } from '../../resources/utils';
import { useDispatch } from 'react-redux';
import { sendToUserPushAsync } from '../../redux/slices/usersSlice';
import { User } from '../../resources/types/usersTypes';
import UserPushHistoryContent from '../UserPushHistoryModal/UserPushHistoryContent';

interface Props {
  user: User;
}

const UserPushContent = (props: Props) => {
  const { user } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [text, setText] = useState<string>('');
  const [confirmDialog, setConfirmDialog] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(3);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (confirmDialog && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [confirmDialog, countdown]);

  const handleSubmit = () => {
    if (!text) {
      alert(t('userModal.enterText'));
    } else {
      setConfirmDialog(true);
      setCountdown(3);
    }
  };

  const handleConfirm = async () => {
    try {
      dispatch(sendToUserPushAsync(user?._id, text, () => {
        alert(t('userModal.pushSentSuccess'));
        setText('');
        setConfirmDialog(false);
      }));
    } catch (e: any) {
      alert(t('userModal.error'));
      setConfirmDialog(false);
    }
  };

  const handleCancel = () => {
    setConfirmDialog(false);
    setCountdown(3);
  };

  return (
    <Box>
      <Box sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {t('userModal.sendTo')} {user?.firstName} {user?.lastName} {user?.phone}
        </Typography>
        <TextField
          rows={8}
          fullWidth
          multiline
          required
          label={t('userModal.text')}
          margin='normal'
          value={text}
          onChange={(e: InputChangeEvent) => setText(e.target.value)}
        />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSubmit}>{t('userModal.send')}</Button>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box>
        <Typography variant="h6" sx={{ px: 3, mb: 2, fontWeight: 600 }}>
          {t('userModal.sentHistory')}
        </Typography>
        {user?._id && <UserPushHistoryContent userId={user._id} />}
      </Box>

      <Dialog
        open={confirmDialog}
        onClose={handleCancel}
      >
        <DialogTitle>{t('userModal.confirmation')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('userModal.confirmSendQuestion')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>
            {t('userModal.cancel')}
          </Button>
          <Button 
            color="primary" 
            variant="contained"
            onClick={handleConfirm}
            disabled={countdown > 0}
          >
            {countdown > 0 ? t('userModal.yesCountdown', { count: countdown }) : t('userModal.yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserPushContent;
